import React, { useState, useEffect } from 'react';
import { Map, CheckCircle, XCircle, Plus, Trash2, ExternalLink, Wrench } from 'lucide-react';
import { getServiceFiles } from '../../utils/autoServiceScanner';

interface SitemapConfigProps {
  formData: any;
  handleConfigChange: (path: string, value: any) => void;
}

const sections = [
  { key: 'home', label: 'Page d\'accueil', defaultPriority: 1.0, defaultChangefreq: 'weekly' },
  { key: 'about', label: 'À propos', defaultPriority: 0.8, defaultChangefreq: 'weekly' },
  { key: 'contact', label: 'Contact', defaultPriority: 0.8, defaultChangefreq: 'weekly' },
  { key: 'contests', label: 'Concours / Votes', defaultPriority: 0.9, defaultChangefreq: 'daily' },
  { key: 'blog', label: 'Blog', defaultPriority: 0.8, defaultChangefreq: 'weekly' },
  { key: 'events', label: 'Événements', defaultPriority: 0.8, defaultChangefreq: 'weekly' },
  { key: 'shop', label: 'Boutique', defaultPriority: 0.9, defaultChangefreq: 'daily' },
  { key: 'portfolio', label: 'Portfolio', defaultPriority: 0.9, defaultChangefreq: 'daily' },
  { key: 'elearning', label: 'E-learning', defaultPriority: 0.9, defaultChangefreq: 'weekly' },
  { key: 'psy', label: 'Psy (Praticiens)', defaultPriority: 0.8, defaultChangefreq: 'monthly' },
  { key: 'awards', label: 'Awards / Distinctions', defaultPriority: 0.8, defaultChangefreq: 'weekly' },
];

const changefreqOptions = [
  { value: 'always', label: 'Toujours (always)' },
  { value: 'hourly', label: 'Toutes les heures (hourly)' },
  { value: 'daily', label: 'Quotidien (daily)' },
  { value: 'weekly', label: 'Hebdomadaire (weekly)' },
  { value: 'monthly', label: 'Mensuel (monthly)' },
  { value: 'yearly', label: 'Annuel (yearly)' },
  { value: 'never', label: 'Jamais (never)' },
];

export const SitemapConfig: React.FC<SitemapConfigProps> = ({ formData, handleConfigChange }) => {
  const sitemapConfig = formData.config?.sitemap || { enabled: true, sections: {}, customPages: [], services: { enabled: true, priority: 0.9, changefreq: 'weekly' } };
  const [newPageUrl, setNewPageUrl] = useState('');
  const [newPagePriority, setNewPagePriority] = useState(0.8);
  const [newPageChangefreq, setNewPageChangefreq] = useState('weekly');
  const [servicePages, setServicePages] = useState<any[]>([]);

  // Détecter automatiquement les pages de services
  useEffect(() => {
    const servicesFolder = formData.config?.services?.folder;
    const servicesRoutes = formData.config?.services?.routes || {};
    
    if (servicesFolder) {
      const serviceFiles = getServiceFiles(servicesFolder);
      const pages = serviceFiles.map(file => ({
        key: file.key,
        label: file.label,
        url: `/services/${servicesRoutes[file.key] || file.defaultRoute}`
      }));
      setServicePages(pages);
    } else {
      setServicePages([]);
    }
  }, [formData.config?.services?.folder, formData.config?.services?.routes]);

  const getSectionConfig = (key: string) => ({
    enabled: sitemapConfig.sections?.[key]?.enabled !== false,
    priority: sitemapConfig.sections?.[key]?.priority || sections.find(s => s.key === key)?.defaultPriority || 0.8,
    changefreq: sitemapConfig.sections?.[key]?.changefreq || sections.find(s => s.key === key)?.defaultChangefreq || 'weekly',
  });

  const updateSectionConfig = (key: string, field: string, value: any) => {
    const currentSections = sitemapConfig.sections || {};
    const currentSection = currentSections[key] || {};
    
    handleConfigChange('sitemap.sections', {
      ...currentSections,
      [key]: {
        ...currentSection,
        [field]: value
      }
    });
  };

  const domain = formData.domain_name || 'votre-domaine.com';

  const addCustomPage = () => {
    if (!newPageUrl.trim()) return;
    
    const customPages = sitemapConfig.customPages || [];
    const newPage = {
      url: newPageUrl.trim(),
      priority: newPagePriority,
      changefreq: newPageChangefreq
    };
    
    handleConfigChange('sitemap.customPages', [...customPages, newPage]);
    setNewPageUrl('');
    setNewPagePriority(0.8);
    setNewPageChangefreq('weekly');
  };

  const removeCustomPage = (index: number) => {
    const customPages = sitemapConfig.customPages || [];
    handleConfigChange('sitemap.customPages', customPages.filter((_: any, i: number) => i !== index));
  };

  const getServicesConfig = () => ({
    enabled: sitemapConfig.services?.enabled !== false,
    priority: sitemapConfig.services?.priority || 0.9,
    changefreq: sitemapConfig.services?.changefreq || 'weekly',
  });

  const updateServicesConfig = (field: string, value: any) => {
    const currentServices = sitemapConfig.services || {};
    handleConfigChange('sitemap.services', {
      ...currentServices,
      [field]: value
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center space-x-3 mb-6">
        <Map className="w-6 h-6 text-blue-500" />
        <h3 className="text-lg font-medium text-white border-b border-white/10 pb-2 flex-1">
          Configuration du Sitemap
        </h3>
      </div>

      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
        <p className="text-sm text-blue-200">
          <strong>💡 Info :</strong> Le sitemap est généré dynamiquement à l'adresse{' '}
          <code className="bg-blue-900/30 px-2 py-1 rounded">https://{domain}/sitemap.xml</code>.
          Configurez les sections à inclure et leur priorité SEO.
        </p>
      </div>

      {/* Activation globale */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-white">Activer le sitemap</h4>
            <p className="text-xs text-gray-400 mt-1">Générer automatiquement le sitemap.xml</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={sitemapConfig.enabled !== false}
              onChange={(e) => handleConfigChange('sitemap.enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-200 mb-4">Sections à inclure dans le sitemap</h4>
        
        <div className="grid grid-cols-1 gap-4">
          {sections.map(section => {
            const config = getSectionConfig(section.key);
            return (
              <div key={section.key} className="bg-white/5 border border-white/10 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    {config.enabled ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-500" />
                    )}
                    <div>
                      <h5 className="text-sm font-semibold text-white">{section.label}</h5>
                      <p className="text-xs text-gray-400">(tenant.config.sitemap.sections.{section.key})</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.enabled}
                      onChange={(e) => updateSectionConfig(section.key, 'enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {config.enabled && (
                  <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-white/10">
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        Priorité (0.0 - 1.0)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={config.priority}
                        onChange={(e) => updateSectionConfig(section.key, 'priority', parseFloat(e.target.value) || 0.8)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">
                        Fréquence de changement
                      </label>
                      <select
                        value={config.changefreq}
                        onChange={(e) => updateSectionConfig(section.key, 'changefreq', e.target.value)}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        style={{ colorScheme: 'dark' }}
                      >
                        {changefreqOptions.map(opt => (
                          <option key={opt.value} value={opt.value} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Section Services (automatique) */}
      {servicePages.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-200 mb-4 flex items-center">
            <Wrench className="w-5 h-5 mr-2 text-blue-400" />
            Pages Services (automatique)
          </h4>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-200 flex items-center gap-2">
              <span className="text-blue-400">💡</span>
              <span>
                <strong>{servicePages.length} page{servicePages.length > 1 ? 's' : ''} de services détectée{servicePages.length > 1 ? 's' : ''}</strong> automatiquement depuis la configuration dans Templates → Configuration des Services
              </span>
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                {getServicesConfig().enabled ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-gray-500" />
                )}
                <div>
                  <h5 className="text-sm font-semibold text-white">Inclure les services dans le sitemap</h5>
                  <p className="text-xs text-gray-400">(tenant.config.sitemap.services)</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={getServicesConfig().enabled}
                  onChange={(e) => updateServicesConfig('enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {getServicesConfig().enabled && (
              <>
                <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-white/10 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">
                      Priorité (0.0 - 1.0)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.1"
                      value={getServicesConfig().priority}
                      onChange={(e) => updateServicesConfig('priority', parseFloat(e.target.value) || 0.9)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">
                      Fréquence de changement
                    </label>
                    <select
                      value={getServicesConfig().changefreq}
                      onChange={(e) => updateServicesConfig('changefreq', e.target.value)}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{ colorScheme: 'dark' }}
                    >
                      {changefreqOptions.map(opt => (
                        <option key={opt.value} value={opt.value} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Liste des pages de services */}
                <div className="space-y-2 pt-3 border-t border-white/10">
                  <p className="text-xs font-medium text-gray-400 mb-2">
                    Pages qui seront incluses ({servicePages.length}) :
                  </p>
                  {servicePages.map((page, index) => (
                    <div key={page.key} className="flex items-center space-x-3 bg-blue-500/10 border border-blue-500/20 rounded px-3 py-2">
                      <Wrench className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-white flex-1">{page.label}</span>
                      <span className="text-xs text-gray-400 font-mono">{page.url}</span>
                      <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-0.5 rounded">
                        Auto
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Pages personnalisées */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-200 mb-4 flex items-center">
          <Plus className="w-5 h-5 mr-2" />
          Pages statiques personnalisées
        </h4>

        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <p className="text-sm text-gray-300 mb-4">
            Ajoutez des pages statiques personnalisées à votre sitemap (ex: /services, /tarifs, /mentions-legales)
          </p>

          {/* Formulaire d'ajout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4">
            <div className="md:col-span-5">
              <label className="block text-xs font-medium text-gray-300 mb-1">URL de la page</label>
              <input
                type="text"
                value={newPageUrl}
                onChange={(e) => setNewPageUrl(e.target.value)}
                placeholder="/ma-page-personnalisee"
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && addCustomPage()}
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-300 mb-1">Priorité</label>
              <input
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={newPagePriority}
                onChange={(e) => setNewPagePriority(parseFloat(e.target.value) || 0.8)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-300 mb-1">Fréquence</label>
              <select
                value={newPageChangefreq}
                onChange={(e) => setNewPageChangefreq(e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ colorScheme: 'dark' }}
              >
                {changefreqOptions.map(opt => (
                  <option key={opt.value} value={opt.value} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-1 flex items-end">
              <button
                onClick={addCustomPage}
                className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Liste des pages personnalisées */}
          {sitemapConfig.customPages && sitemapConfig.customPages.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-400 mb-2">Pages ajoutées :</p>
              {sitemapConfig.customPages.map((page: any, index: number) => (
                <div key={index} className="flex items-center justify-between bg-gray-800/50 rounded px-3 py-2">
                  <div className="flex items-center space-x-3 flex-1">
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-white font-mono">{page.url}</span>
                    <span className="text-xs text-gray-400">
                      Priority: {page.priority} | {page.changefreq}
                    </span>
                  </div>
                  <button
                    onClick={() => removeCustomPage(index)}
                    className="p-1 hover:bg-red-500/20 rounded transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Informations SEO */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-white mb-2">📊 À propos des sitemaps</h4>
        <ul className="text-xs text-gray-300 space-y-1">
          <li>• <strong>Priorité :</strong> Indique l'importance relative des pages (0.0 = faible, 1.0 = élevée)</li>
          <li>• <strong>Changefreq :</strong> Fréquence estimée de mise à jour du contenu</li>
          <li>• <strong>Lastmod :</strong> Date de dernière modification (générée automatiquement)</li>
          <li>• <strong>Indexation :</strong> Soumettez votre sitemap à Google Search Console et Bing Webmaster Tools</li>
        </ul>
      </div>

      {/* Aperçu URL */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-white mb-3">🔗 URLs de votre sitemap</h4>
        <div className="space-y-2">
          <div className="bg-gray-900 rounded px-4 py-2 font-mono text-sm text-green-400">
            https://{domain}/sitemap.xml
          </div>
          <div className="bg-gray-900 rounded px-4 py-2 font-mono text-sm text-green-400">
            https://{domain}/sitemap
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Ajoutez l'une de ces URLs dans votre fichier robots.txt et dans les outils pour webmasters
        </p>
        <p className="text-xs text-yellow-400 mt-2">
          ⚠️ Si vous voyez un ancien sitemap, exécutez : <code className="bg-gray-800 px-2 py-1 rounded">.\scripts\clean-static-sitemap.ps1</code>
        </p>
      </div>
    </div>
  );
};
